import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { Layout, PageHeader } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Membership() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("month");
  const [busy, setBusy] = useState("");
  const [promo, setPromo] = useState("");

  useEffect(() => { api.get("/membership-plans").then((r) => setPlans(r.data)); }, []);

  const subscribe = async (plan) => {
    if (plan.tier === "free") { toast.success("You're on the Free plan"); return; }
    if (!user) { toast.error("Sign in to subscribe"); nav("/login"); return; }
    setBusy(plan.id);
    try {
      const { data } = await api.post("/payments/checkout", { kind: "membership", plan_id: plan.id, interval, promo_code: promo.trim() || undefined, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(err.response?.data?.detail || "Could not start checkout"); setBusy(""); }
  };

  return (
    <Layout>
      <PageHeader label="Membership" title="Choose Your Tier"
        subtitle="Unlock early litter access, member pricing, the full Academy and priority reservations." />
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12">
        <div className="flex justify-center mb-12">
          <div className="inline-flex border border-eon-border p-1">
            {["month", "year"].map((iv) => (
              <button key={iv} onClick={() => setInterval(iv)} data-testid={`billing-${iv}`}
                className={`text-xs font-bold uppercase tracking-wider px-6 py-2.5 transition-colors ${interval === iv ? "bg-eon-gold text-white" : "text-eon-text/70"}`}>
                {iv === "month" ? "Monthly" : "Annual · Save 20%"}
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-center mb-10">
          <div className="flex items-center gap-2 border border-eon-border rounded-full px-4 py-2 bg-white">
            <span className="text-xs uppercase tracking-wider text-eon-muted">Promo code</span>
            <input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder="e.g. EARLYBIRD30"
              data-testid="promo-input" className="bg-transparent text-sm text-eon-text outline-none w-40 uppercase placeholder:normal-case placeholder:text-eon-muted" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {plans.map((p) => {
            const price = interval === "month" ? p.price_monthly : p.price_yearly;
            const highlight = p.popular;
            const current = user?.membership_tier === p.tier;
            return (
              <div key={p.id} data-testid={`plan-${p.tier}`} className={`eon-card p-8 flex flex-col relative ${highlight ? "border-eon-gold" : ""}`}>
                {highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 eon-label bg-eon-gold text-white px-3 py-1">Most Popular</div>}
                <h3 className="font-display text-3xl font-black uppercase">{p.name}</h3>
                {p.tagline && <div className="text-sm text-eon-muted mt-1">{p.tagline}</div>}
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-mono text-4xl text-eon-gold font-medium">${price}</span>
                  {price > 0 && <span className="text-sm text-eon-muted">/{interval === "month" ? "mo" : "yr"}</span>}
                </div>
                {p.compare && <div className="text-xs text-eon-muted mt-1">Compared to <span className="line-through">{p.compare}</span></div>}
                <button onClick={() => subscribe(p)} disabled={busy === p.id || current} data-testid={`subscribe-${p.tier}`}
                  className={`mt-6 text-sm font-bold uppercase tracking-wider py-3.5 transition-colors disabled:opacity-50 ${highlight ? "bg-eon-gold text-white hover:bg-eon-goldhover" : p.tier === "free" ? "border border-eon-border text-eon-text hover:border-eon-gold" : "bg-eon-elevated text-eon-text hover:bg-eon-gold hover:text-black"}`}>
                  {current ? "Current Plan" : busy === p.id ? "Redirecting..." : p.tier === "free" ? "Get Started Free" : `Get ${p.name}`}
                </button>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (<li key={f} className="flex items-start gap-3 text-sm text-eon-text/80"><Check size={16} className="text-eon-gold shrink-0 mt-0.5" /> {f}</li>))}
                  {(p.coming || []).map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-eon-muted"><Check size={16} className="text-eon-muted shrink-0 mt-0.5" />
                      <span>{f} <span className="ml-1 text-[9px] font-bold uppercase tracking-wider bg-eon-elevated border border-eon-border px-1.5 py-0.5 align-middle">Coming Soon</span></span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-eon-muted mt-10">Secure payments by Stripe. Cancel anytime. Test card: 4242 4242 4242 4242.</p>
      </div>
    </Layout>
  );
}
